import React, { useState, useRef, useEffect, useCallback, useContext } from 'react';
import { debounce } from 'lodash';
import StepIndicator from '../basic/MyStepIndicator';
import MyButton from '../basic/MyButton';
import MyTextInput from '../basic/MyTextInput';
import MyDropdownGeneric from '../basic/MyDropdownGeneric';
import MyLoading from '../basic/MyLoading';
import MyCustomIcon from '../basic/MyCustomIcon';
import { currencies } from '../basic/currencies';
import { timezones } from '../basic/timezones';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../../context/UserContext';
import { verifyEmail, verifyCode as verifyCodeAPI, completeOnboarding, checkEmailExistence, checkUsernameExistence, loginUser } from '../../api';
import './RegistrationForm.css';

const RegistrationForm = () => {
    const [currentStep, setCurrentStep] = useState(0);
    const [formData, setFormData] = useState({
        email: '',
        verificationCode: '',
        displayName: '',
        username: '',
        password: '',
        confirmPassword: '',
        familyName: '',
        currency: 'USD - US Dollar',
        timezone: 'America/Los_Angeles - Pacific Time - Los Angeles',
    });

    const [verificationCodeSent, setVerificationCodeSent] = useState(false);
    const [verificationCodeExpiry, setVerificationCodeExpiry] = useState(null);
    const [resendCooldown, setResendCooldown] = useState(0);
    const [emailVerified, setEmailVerified] = useState(false);
    const [verificationError, setVerificationError] = useState('');
    const [passwordsMatch, setPasswordsMatch] = useState(true);
    const [isLoading, setIsLoading] = useState(false);

    const [emailExists, setEmailExists] = useState(false);
    const [usernameExists, setUsernameExists] = useState(false);
    const [isCheckingEmail, setIsCheckingEmail] = useState(false);
    const [isCheckingUsername, setIsCheckingUsername] = useState(false);
    const [emailMessage, setEmailMessage] = useState('');
    const [usernameMessage, setUsernameMessage] = useState('');

    const steps = ["Enter Email", "Verify Email", "Your Information", "Family Settings", "Review and Submit"];
    const formRef = useRef(null);
    const [formHeight, setFormHeight] = useState(0);

    const emailInputRef = useRef(null);
    const verificationCodeInputRef = useRef(null);
    const displayNameInputRef = useRef(null);
    const familyNameInputRef = useRef(null);

    const [isResending, setIsResending] = useState(false);

    const navigate = useNavigate();
    const { login } = useContext(UserContext);

    useEffect(() => {
        if (formRef.current && currentStep === 0) {
            setFormHeight(formRef.current.offsetHeight);
        }
    }, [currentStep]);

    const [remainingTime, setRemainingTime] = useState(0);

    useEffect(() => {
        let timer;
        if (resendCooldown > 0 || verificationCodeExpiry) {
            timer = setInterval(() => {
                if (resendCooldown > 0) {
                    setResendCooldown(prev => prev - 1);
                }
                if (verificationCodeExpiry) {
                    const remaining = Math.max(0, Math.floor((verificationCodeExpiry - Date.now()) / 1000));
                    setRemainingTime(remaining);
                    if (remaining === 0) {
                        setVerificationCodeExpiry(null);
                    }
                }
            }, 1000);
        }
        return () => clearInterval(timer);
    }, [resendCooldown, verificationCodeExpiry]);

    useEffect(() => {
        setPasswordsMatch(formData.password === formData.confirmPassword);
    }, [formData.password, formData.confirmPassword]);

    useEffect(() => {
        const focusMap = {
            0: emailInputRef,
            1: verificationCodeInputRef,
            2: displayNameInputRef,
            3: familyNameInputRef
        };
        const inputToFocus = focusMap[currentStep];
        if (inputToFocus && inputToFocus.current) {
            inputToFocus.current.focus();
        }
    }, [currentStep]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        if (name === 'email') {
            sequentialEmailCheck(value);
        } else if (name === 'username') {
            sequentialUsernameCheck(value);
        }
    };

    const handleDropdownChange = (name) => (value) => {
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleNext = () => {
        if (currentStep < steps.length - 1) {
            setCurrentStep(prev => prev + 1);
        }
    };

    const handleBack = () => {
        if (currentStep > 0) {
            setCurrentStep(prev => prev - 1);
        }
    };

    const handleFormSubmit = (e) => {
        if (currentStep !== steps.length - 1) {
            e.preventDefault();
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (currentStep !== steps.length - 1) {
            return;  // Don't submit if not on the last step
        }
        setIsLoading(true);
        try {
            const onboardingData = {
                currency: formData.currency.split(' - ')[0],
                email: formData.email,
                family_name: formData.familyName,
                parent_display_name: formData.displayName,
                password: formData.password,
                timezone: formData.timezone.split(' - ')[0],
                username: formData.username
            };
            await completeOnboarding(onboardingData);
            
            // Automatically log in after successful registration
            const loginData = await loginUser(formData.username, formData.password);
            login(loginData.token, loginData.user || { identifier: formData.username });
            
        } catch (error) {
            console.error('Error completing onboarding:', error);
            setVerificationError('Failed to complete onboarding. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleGlobalKeyDown = (e) => {
        if (e.key === 'Enter' && currentStep !== steps.length - 1) {
            e.preventDefault();
        }
    };

    const validateEmail = (email) => {
        const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return re.test(String(email).toLowerCase());
    };

    const sequentialEmailCheck = useCallback(
        debounce(async (email) => {
            setIsCheckingEmail(false);
            setEmailExists(false);
            setEmailMessage('');
    
            if (email.trim() === '') {
                return;
            }
    
            if (!validateEmail(email)) {
                setEmailMessage('Please enter a valid email address');
                setEmailExists(true);
                return;
            }
    
            setIsCheckingEmail(true);
            setEmailMessage('Checking email...');
            
            try {
                const { exists } = await checkEmailExistence(email);
                setEmailExists(exists);
                setEmailMessage(exists 
                    ? 'This Email is already registered, try another email address.'
                    : 'This Email is available.');
            } catch (error) {
                console.error('Error checking email existence:', error);
                setEmailMessage('Error checking email. Please try again.');
                setEmailExists(true);
            } finally {
                setIsCheckingEmail(false);
            }
        }, 300),
        []
    );

    const sequentialUsernameCheck = useCallback(
        debounce(async (username) => {
            if (username.includes(' ')) {
                setUsernameMessage('Usernames cannot have spaces');
                setUsernameExists(true);
                return;
            }
    
            if (username.length >= 4) {
                setIsCheckingUsername(true);
                setUsernameMessage('Checking username...');
                try {
                    const response = await checkUsernameExistence(username);
                    if (response && typeof response.exists === 'boolean') {
                        setUsernameExists(response.exists);
                        setUsernameMessage(response.exists
                            ? 'This username is taken, create a unique username.'
                            : 'Username is available.');
                    } else {
                        console.error('Unexpected response format:', response);
                        setUsernameMessage('Error checking username. Please try again.');
                        setUsernameExists(true);
                    }
                } catch (error) {
                    console.error('Error checking username existence:', error);
                    setUsernameMessage('Error checking username. Please try again.');
                    setUsernameExists(true);
                } finally {
                    setIsCheckingUsername(false);
                }
            } else {
                setUsernameMessage('Username must be at least 4 characters long.');
                setUsernameExists(true);
            }
        }, 300),
        []
    );

    const sendVerificationCode = async () => {
        if (validateEmail(formData.email) && !emailExists) {
            setIsLoading(true);
            try {
                const response = await verifyEmail(formData.email);
                console.log('Email verification initiated:', response);
                setVerificationCodeSent(true);
                setVerificationCodeExpiry(Date.now() + 15 * 60 * 1000);
                setResendCooldown(60);
                
                if (!isResending) {
                    handleNext();
                    setIsResending(true);
                } else {
                    setVerificationError("A new code has been sent. Please check your email.");
                }
            } catch (error) {
                console.error('Error sending verification code:', error);
                setVerificationError("Failed to send verification code. Please try again.");
            } finally {
                setIsLoading(false);
            }
        } else {
            setVerificationError('Please enter a valid and available email address.');
        }
    };

    const verifyCode = async () => {
        setIsLoading(true);
        try {
            const response = await verifyCodeAPI(formData.verificationCode, formData.email);
            console.log('Code verified successfully:', response);
            setEmailVerified(true);
            setVerificationError('');
            handleNext();
        } catch (error) {
            console.error('Error verifying code:', error);
            setVerificationError("Invalid verification code. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const isYourInformationStepValid = () => {
        return formData.displayName.trim() !== '' &&
               formData.username.trim() !== '' &&
               formData.password.trim() !== '' &&
               formData.confirmPassword.trim() !== '' &&
               passwordsMatch &&
               !usernameExists &&
               !isCheckingUsername;
    };

    const isFamilySettingsStepValid = () => {
        return formData.familyName.trim() !== '' &&
               formData.currency.trim() !== '' &&
               formData.timezone.trim() !== '';
    };

    const renderEmailStep = () => (
        <>
            <h3>Let's start by verifying your email</h3>
            <div className="input-group">
                <MyTextInput 
                    label="Email" 
                    name="email" 
                    type="email" 
                    value={formData.email} 
                    onChange={handleInputChange} 
                    required 
                    ref={emailInputRef} 
                />
                {isCheckingEmail && (
                    <div className="indicator-wrapper">
                        <MyLoading size="small" />
                    </div>
                )}
                {emailMessage && !isCheckingEmail && (
                    <div className="indicator-wrapper">
                        <div className="indicator-content">
                            {emailExists ? (
                                <span className="error-message">{emailMessage}</span>
                            ) : (
                                <>
                                    <MyCustomIcon type="check" color="green" />
                                    <span className="success-text">{emailMessage}</span>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>
            <MyButton 
                text="Send Verification Code" 
                onClick={sendVerificationCode} 
                disabled={!validateEmail(formData.email) || emailExists || isCheckingEmail || isLoading} 
            />
            {verificationError && <p className="error-message">{verificationError}</p>}
        </>
    );

    const renderVerificationStep = () => (
        <>
            <h3>Verify Email</h3>
            <p>A verification code has been sent to <strong>{formData.email}</strong></p>
            <MyTextInput label="Verification Code" name="verificationCode" value={formData.verificationCode} onChange={handleInputChange} required ref={verificationCodeInputRef} />
            {verificationError && <p className="error-message">{verificationError}</p>}
            <MyButton text="Verify Code" onClick={verifyCode} disabled={formData.verificationCode.length !== 6 || isLoading} />
            <MyButton 
                text={resendCooldown > 0 ? `Resend Code (${resendCooldown}s)` : "Resend Code"} 
                onClick={sendVerificationCode} 
                disabled={resendCooldown > 0 || isLoading} 
            />
            {verificationCodeExpiry && (
                <p className="expiry-text">
                    Code expires in {remainingTime} seconds
                </p>
            )}
        </>
    );

    const renderParentInformation = () => (
        <>
            <h3>Parent / Guardian Information</h3>
            <MyTextInput label="Display Name - How the kids call you?" name="displayName" value={formData.displayName} onChange={handleInputChange} required ref={displayNameInputRef} />
            <div className="input-group">
                <MyTextInput 
                    label="Username - Used for log-in" 
                    name="username" 
                    value={formData.username} 
                    onChange={handleInputChange} 
                    required 
                />
                {isCheckingUsername && (
                    <div className="indicator-wrapper">
                        <MyLoading size="small" />
                    </div>
                )}
                {usernameMessage && !isCheckingUsername && (
                    <div className="indicator-wrapper">
                        <div className="indicator-content">
                            {usernameExists ? (
                                <span className="error-message">{usernameMessage}</span>
                            ) : (
                                <>
                                    <MyCustomIcon type="check" color="green" />
                                    <span className="success-text">{usernameMessage}</span>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>
            <MyTextInput label="Password" name="password" type="password" value={formData.password} onChange={handleInputChange} required />
            <MyTextInput label="Confirm Password" name="confirmPassword" type="password" value={formData.confirmPassword} onChange={handleInputChange} required />
            {!passwordsMatch && <p className="error-message">Passwords do not match</p>}
            </>
            );

            const renderFamilySettings = () => (
            <>
            <h3>Family Settings</h3>
            <MyTextInput label="Family Name" name="familyName" value={formData.familyName} onChange={handleInputChange} required ref={familyNameInputRef} />
            <MyDropdownGeneric
                label="Currency"
                options={currencies.map(c => `${c.code} - ${c.name}`)}
                value={formData.currency}
                onChange={handleDropdownChange('currency')}
            />
            <MyDropdownGeneric
                label="Timezone"
                options={timezones.map(t => `${t.code} - ${t.name}`)}
                value={formData.timezone}
                onChange={handleDropdownChange('timezone')}
            />
            </>
            );

            const renderReview = () => (
            <>
            <h3>Review</h3>
            <p><strong>Email:</strong> {formData.email} (Verified)</p>
            <p><strong>Parent Display Name:</strong> {formData.displayName}</p>
            <p><strong>Username:</strong> {formData.username}</p>
            <p><strong>Password:</strong> {formData.password.replace(/./g, '*')}</p>
            <p><strong>Family Name:</strong> {formData.familyName}</p>
            <p><strong>Currency:</strong> {formData.currency}</p>
            <p><strong>Timezone:</strong> {formData.timezone}</p>
            </>
            );

            const renderStepContent = () => {
            switch (currentStep) {
            case 0: return renderEmailStep();
            case 1: return renderVerificationStep();
            case 2: return renderParentInformation();
            case 3: return renderFamilySettings();
            case 4: return renderReview();
            default: return null;
            }
            };

            return (
            <div className="registration-container" onKeyDown={handleGlobalKeyDown}>
            <div className="onboarding-workflow">
                <h2>New Family Onboarding</h2>
                <StepIndicator steps={steps} currentStep={currentStep} />
                <form onSubmit={handleFormSubmit} ref={formRef} style={{ minHeight: `${formHeight}px`, display: 'flex', flexDirection: 'column' }}>
                    <div className="form-content" style={{ flex: 1 }}>
                        {renderStepContent()}
                    </div>
                    <div className="onboarding-actions">
                        {currentStep > 1 && currentStep < steps.length - 1 && (
                            <MyButton 
                                text="Next" 
                                onClick={handleNext} 
                                disabled={
                                    (currentStep === 2 && !isYourInformationStepValid()) ||
                                    (currentStep === 3 && !isFamilySettingsStepValid()) ||
                                    isLoading
                                } 
                            />
                        )}
                        {currentStep === steps.length - 1 && (
                            <MyButton text="Submit" onClick={handleSubmit} disabled={isLoading} />
                        )}
                        {currentStep > 0 && <MyButton text="Back" onClick={handleBack} disabled={isLoading} />}
                    </div>
                </form>
            </div>
            </div>
            );
            };

            export default RegistrationForm;